import React, { useCallback, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Upload, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import { CSVDocumentation } from './CSVDocumentation';
import { ValidationErrors, ValidationError } from './ValidationErrors';
import { BuildingData } from './Step3Buildings';
import { useTranslation } from 'react-i18next';
import { apartmentNumberKey } from '@/lib/naming';

export interface ClientData {
  meter_id: string;
  apartment_number: string;
  building_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

interface Step4CSVImportProps {
  clientsData: {
    csvFile: File | null;
    parsedData: ClientData[];
    validationErrors: ValidationError[];
    options: {
      sendWelcomeEmails: boolean;
      autoGenerateClientNumbers: boolean;
      createAccountsImmediately: boolean;
    };
  };
  buildings: BuildingData[];
  onChange: (field: string, value: ClientData[]) => void;
}

export const Step4CSVImport: React.FC<Step4CSVImportProps> = ({ clientsData, buildings, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateCSVData = useCallback((data: Record<string, string>[]): { valid: ClientData[]; errors: ValidationError[] } => {
    const errors: ValidationError[] = [];
    const validData: ClientData[] = [];
    const seenEmails = new Set<string>();
    const seenMeterIds = new Set<string>();
    // Apartment number key -> building (lower case) of the first row using it.
    // Numbers are unique per organization, so the same number in two
    // buildings is an error; the same number twice in one building is the
    // same unit and is allowed.
    const numberBuilding = new Map<string, string>();

    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because CSV has header row and index starts at 0

      if (row.apartment_number?.trim() && row.building_name?.trim()) {
        const key = apartmentNumberKey(row.apartment_number);
        const building = row.building_name.trim().toLowerCase();
        const first = numberBuilding.get(key);
        if (first === undefined) numberBuilding.set(key, building);
        else if (first !== building) {
          errors.push({
            row: rowNum,
            field: 'apartment_number',
            message: t('onboarding.step4.duplicateApartmentNumber', { number: row.apartment_number.trim() }),
          });
        }
      }

      // Check required fields: meter_id, apartment_number, building_name
      if (!row.meter_id?.trim()) {
        errors.push({ row: rowNum, field: 'meter_id', message: t('onboarding.step4.meterIdRequired') });
      } else if (seenMeterIds.has(row.meter_id.trim().toUpperCase())) {
        errors.push({ row: rowNum, field: 'meter_id', message: t('onboarding.step4.duplicateMeterId') });
      } else {
        seenMeterIds.add(row.meter_id.trim().toUpperCase());
      }

      if (!row.apartment_number?.trim()) {
        errors.push({ row: rowNum, field: 'apartment_number', message: t('onboarding.step4.apartmentRequired') });
      }

      if (!row.building_name?.trim()) {
        errors.push({ row: rowNum, field: 'building_name', message: t('onboarding.step4.buildingRequired') });
      } else if (!buildings.find(b => b.name.toLowerCase() === row.building_name.trim().toLowerCase())) {
        errors.push({ row: rowNum, field: 'building_name', message: t('onboarding.step4.buildingNotFound', { building: row.building_name }) });
      }

      // Validate email format if provided (optional)
      if (row.email?.trim()) {
        if (!validateEmail(row.email.trim())) {
          errors.push({ row: rowNum, field: 'email', message: t('onboarding.step4.invalidEmail') });
        } else if (seenEmails.has(row.email.trim().toLowerCase())) {
          errors.push({ row: rowNum, field: 'email', message: t('onboarding.step4.duplicateEmail') });
        } else {
          seenEmails.add(row.email.trim().toLowerCase());
        }
      }

      // If all required fields are valid, add to valid data
      if (row.meter_id?.trim() && row.apartment_number?.trim() && row.building_name?.trim() &&
          buildings.find(b => b.name.toLowerCase() === row.building_name.trim().toLowerCase()) &&
          !errors.some(e => e.row === rowNum)) {
        validData.push({
          meter_id: row.meter_id.trim(),
          apartment_number: row.apartment_number.trim(),
          building_name: row.building_name.trim(),
          first_name: row.first_name?.trim(),
          last_name: row.last_name?.trim(),
          email: row.email?.trim(),
          phone: row.phone?.trim(),
        });
      }
    });

    return { valid: validData, errors };
  }, [buildings, t]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert(t('onboarding.step4.fileTooLarge'));
      return;
    }

    if (!file.name.endsWith('.csv')) {
      alert(t('onboarding.step4.mustBeCsv'));
      return;
    }

    onChange('csvFile', file);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { valid, errors } = validateCSVData(results.data);
        onChange('parsedData', valid);
        onChange('validationErrors', errors);
      },
      error: (error) => {
        alert(t('onboarding.step4.parseError', { message: error.message }));
        onChange('csvFile', null);
      }
    });
  }, [onChange, validateCSVData, t]);

  // Re-validate CSV data when buildings change (e.g., user goes back to Step 3 and modifies buildings)
  useEffect(() => {
    if (clientsData.parsedData.length > 0) {
      // Re-validate existing parsed data against updated buildings
      const dataToRevalidate = clientsData.parsedData.map(client => ({
        meter_id: client.meter_id,
        apartment_number: client.apartment_number,
        building_name: client.building_name,
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
      }));

      const { valid, errors } = validateCSVData(dataToRevalidate);
      onChange('parsedData', valid);
      onChange('validationErrors', errors);
    }
  }, [buildings]); // Only re-run when buildings array changes

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileSpreadsheet className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('onboarding.step4.title')}</h2>
          <p className="text-muted-foreground">{t('onboarding.step4.subtitle')}</p>
        </div>
      </div>

      <CSVDocumentation />

      <div className="border rounded-lg p-6 bg-card space-y-4">
        <h3 className="font-semibold">{t('onboarding.step4.uploadClientData')}</h3>
        
        {clientsData.csvFile ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <p className="font-medium">{clientsData.csvFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {t('onboarding.step4.validClientsFound', { count: clientsData.parsedData.length })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  onChange('csvFile', null);
                  onChange('parsedData', []);
                  onChange('validationErrors', []);
                  // Reset file input so same file can be re-selected
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="text-destructive hover:underline text-sm"
              >
                {t('onboarding.step4.remove')}
              </button>
            </div>

            <ValidationErrors errors={clientsData.validationErrors} />

            {clientsData.parsedData.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted p-2">
                  <p className="text-sm font-medium">{t('onboarding.step4.previewFirst5')}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 text-left">{t('onboarding.step4.colMeterId')}</th>
                        <th className="px-4 py-2 text-left">{t('onboarding.step4.colBuilding')}</th>
                        <th className="px-4 py-2 text-left">{t('onboarding.step4.colApt')}</th>
                        <th className="px-4 py-2 text-left">{t('onboarding.step4.colName')}</th>
                        <th className="px-4 py-2 text-left">{t('onboarding.step4.colEmail')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientsData.parsedData.slice(0, 5).map((client, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-4 py-2 font-mono text-xs">{client.meter_id}</td>
                          <td className="px-4 py-2">{client.building_name}</td>
                          <td className="px-4 py-2">{client.apartment_number}</td>
                          <td className="px-4 py-2">
                            {client.first_name || client.last_name 
                              ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
                              : '-'}
                          </td>
                          <td className="px-4 py-2">{client.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {clientsData.parsedData.length > 5 && (
                  <div className="bg-muted/50 p-2 text-center text-sm text-muted-foreground">
                    {t('onboarding.step4.andMoreClients', { count: clientsData.parsedData.length - 5 })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <label
            htmlFor="csvUpload"
            className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-12 cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <Upload className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">{t('onboarding.step4.clickToUploadCsv')}</p>
            <p className="text-sm text-muted-foreground">{t('onboarding.step4.dragAndDrop')}</p>
            <p className="text-xs text-muted-foreground mt-2">{t('onboarding.step4.maxFileSize')}</p>
          </label>
        )}
        <input
          ref={fileInputRef}
          id="csvUpload"
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      <div className="border rounded-lg p-6 bg-card space-y-4">
        <h3 className="font-semibold">{t('onboarding.step4.importOptions')}</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="sendEmails">{t('onboarding.step4.sendWelcomeEmails')}</Label>
              <p className="text-sm text-muted-foreground">{t('onboarding.step4.sendWelcomeEmailsDesc')}</p>
            </div>
            <Switch
              id="sendEmails"
              checked={clientsData.options.sendWelcomeEmails}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, sendWelcomeEmails: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="autoGenerate">{t('onboarding.step4.autoGenerateNumbers')}</Label>
              <p className="text-sm text-muted-foreground">{t('onboarding.step4.autoGenerateNumbersDesc')}</p>
            </div>
            <Switch
              id="autoGenerate"
              checked={clientsData.options.autoGenerateClientNumbers}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, autoGenerateClientNumbers: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="createAccounts">{t('onboarding.step4.createAccounts')}</Label>
              <p className="text-sm text-muted-foreground">{t('onboarding.step4.createAccountsDesc')}</p>
            </div>
            <Switch
              id="createAccounts"
              checked={clientsData.options.createAccountsImmediately}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, createAccountsImmediately: checked })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
